import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import { getUserData } from "./const";
import {
  getErpAuthState,
  listAdminUsers,
  listSalesTargets,
  saveSalesTargets,
  deleteSalesTarget,
  showBranch,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  LockIconGrey,
  LockIconSizeOnButton,
  LockIconSizeTable,
  LockIconWhiteOnButton,
} from "../utils/LockIconforPermission";

/** Merge legacy encrypted `userData` with ERP `user-details` (same idea as Navbar). */
function mergeSessionUser() {
  const legacy = getUserData();
  const erp = getErpAuthState().user;
  const merged = { ...(legacy || {}), ...(erp || {}) };
  const tenantIdRaw =
    merged.tenant_id ?? merged.tenantId ?? erp?.tenant_id ?? legacy?.tenant_id;
  const branchIdRaw =
    merged.branch_id ?? merged.branchId ?? erp?.branch_id ?? legacy?.branch_id;

  const tenantId = (() => {
    const n = Number(tenantIdRaw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const branchId = (() => {
    const n = Number(branchIdRaw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const tenantNameHint =
    merged.tenant_name ??
    merged.tenantName ??
    merged.tenant ??
    erp?.tenant_name ??
    legacy?.tenant_name ??
    "";
  const branchNameHint =
    merged.branch_name ??
    merged.branchName ??
    merged.branch ??
    erp?.branch_name ??
    legacy?.branch_name ??
    "";

  return {
    merged,
    tenantId,
    branchId,
    tenantNameHint: String(tenantNameHint || "").trim(),
    branchNameHint: String(branchNameHint || "").trim(),
  };
}

/** Map `POST /api/sales_targets/` list (or save) `data` to UI rows — see sales_targets_hrms_api.md A1/A2. */
function mapSalesTargetsListToRows(data, fallbackMonth) {
  const month = String(data?.month ?? fallbackMonth ?? "").trim();
  const shop = data?.shop;
  const branchRows = [];
  if (shop != null && Number(shop.target_amount) > 0) {
    branchRows.push({
      id: shop.id,
      period: month,
      amount: Number(shop.target_amount) || 0,
      note: shop.note != null ? String(shop.note) : "",
    });
  }
  const rawEmp = Array.isArray(data?.employees) ? data.employees : [];
  const employeeRows = rawEmp.map((e) => ({
    id: e.id,
    ua_id: e.user_admin_id,
    employeeName:
      String(e.user_name || "").trim() || `User #${e.user_admin_id}`,
    period: month,
    amount: Number(e.target_amount) || 0,
    note: e.note != null ? String(e.note) : "",
  }));
  return { branchRows, employeeRows };
}

function formatMonthLabel(yyyyMm) {
  const s = String(yyyyMm || "").trim();
  if (!/^\d{4}-\d{2}$/.test(s)) return s || "—";
  try {
    const [y, m] = s.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleString("en-IN", { month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

function formatCurrencyINR(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `₹${v.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/** Use API message when meaningful; ERP often returns generic "radha". */
function getSafeSuccessMessage(apiMessage, fallbackMessage) {
  const msg = String(apiMessage || "").trim();
  if (!msg) return fallbackMessage;
  if (msg.toLowerCase() === "radha") return fallbackMessage;
  return msg;
}

/** Shown in tables when SALES_TARGET_VIEW is missing (no error toast). */
const SALES_TARGET_NO_VIEW_MSG = "No permission to view sales targets.";

const SALES_TARGET_LOCK_ICON = "solar:lock-bold";

function SalesTargetNoViewTableMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={SALES_TARGET_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{SALES_TARGET_NO_VIEW_MSG}</span>
    </span>
  );
}

/** Shown in Actions column when user can view but lacks SALES_TARGET_EDIT. */
function SalesTargetEditLockedHint() {
  return (
    <span
      className="d-inline-flex align-items-center"
      title="No permission to edit sales targets (SALES_TARGET_EDIT)."
      role="img"
      aria-label="No permission to edit sales targets"
    >
      <Icon
        icon={SALES_TARGET_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
    </span>
  );
}

function isSalesTargetListViewDeniedMessage(message) {
  const s = String(message || "").toLowerCase();
  return (
    s.includes(String(PERMISSIONS.SALES_TARGET_VIEW).toLowerCase()) ||
    (s.includes("missing permission") && s.includes("sales_target"))
  );
}

function normalizeEmployeeRow(raw) {
  const st = parseInt(String(raw.status ?? "").trim(), 10);
  const status = Number.isFinite(st) ? st : 0;
  const roles = Array.isArray(raw.roles)
    ? raw.roles
    : raw.role != null
      ? [raw.role]
      : [];

  return {
    ua_id: raw.id ?? raw.ua_id ?? raw.user_id,
    name: String(raw.name || "").trim() || "—",
    phone: String(raw.phone || raw.mobile || "").trim(),
    email: String(raw.email || "").trim(),
    roles,
    status,
    tenant_id:
      raw.tenant_id ??
      raw.tenantId ??
      raw.employee?.tenant_id ??
      raw.tenant?.id ??
      null,
    branch_id:
      raw.branch_id ??
      raw.branchId ??
      raw.employee?.branch_id ??
      raw.branch?.id ??
      raw.default_branch_id ??
      null,
  };
}

function employeeMatchesBranchContext(emp, tenantId, branchId) {
  if (emp.status === 2) return false;
  const b = emp.branch_id;
  if (branchId && b != null && b !== "" && Number(b) !== Number(branchId)) {
    return false;
  }
  const t = emp.tenant_id;
  if (tenantId && t != null && t !== "" && Number(t) !== Number(tenantId)) {
    return false;
  }
  return true;
}

function parseBranchShowNames(res) {
  const d = res?.data;
  const branch =
    d?.branch ??
    (d && typeof d === "object" && (d.name != null || d.branch_name != null)
      ? d
      : null);
  const tenant = d?.tenant ?? branch?.tenant ?? null;
  const branchName =
    branch?.name ?? branch?.branch_name ?? d?.branch_name ?? "";
  const tenantName =
    tenant?.name ??
    tenant?.tenant_name ??
    branch?.tenant_name ??
    d?.tenant_name ??
    "";
  return {
    branchName: String(branchName || "").trim(),
    tenantName: String(tenantName || "").trim(),
  };
}

function SalesTarget() {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();

  const session = mergeSessionUser();
  const { tenantId, tenantNameHint, branchNameHint } = session;
  const branchId = getSessionBranchId() ?? session.branchId;

  const canView = hasPermission(PERMISSIONS.SALES_TARGET_VIEW);
  const canEdit = hasPermission(PERMISSIONS.SALES_TARGET_EDIT);
  /** Viewers see Actions column with disabled controls when they lack SALES_TARGET_EDIT. */
  const showSalesTargetActions = canView;

  const [tenantName, setTenantName] = useState(tenantNameHint);
  const [branchName, setBranchName] = useState(branchNameHint);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  const [selectedMonth, setSelectedMonth] = useState(() =>
    new Date().toISOString().slice(0, 7),
  );
  const [branchRows, setBranchRows] = useState([]);
  const [employeeRows, setEmployeeRows] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  const [showBranchModal, setShowBranchModal] = useState(false);
  const [branchFormAmount, setBranchFormAmount] = useState("");
  const [branchFormNote, setBranchFormNote] = useState("");
  const [editingBranchRowId, setEditingBranchRowId] = useState(null);
  const [savingBranch, setSavingBranch] = useState(false);

  const [showEmpModal, setShowEmpModal] = useState(false);
  const [empSelected, setEmpSelected] = useState(null);
  const [empFormAmount, setEmpFormAmount] = useState("");
  const [empFormNote, setEmpFormNote] = useState("");
  const [editingEmpRowId, setEditingEmpRowId] = useState(null);
  const [savingEmp, setSavingEmp] = useState(false);

  const [deleteShopOpen, setDeleteShopOpen] = useState(false);
  const [deleteEmpUaId, setDeleteEmpUaId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const selectStyles = useMemo(
    () => ({
      control: (p) => ({
        ...p,
        minHeight: 44,
        borderRadius: buttonTableStyle === "square" ? 0 : 8,
        borderColor: "#e0e6eb",
        boxShadow: "none",
      }),
      menu: (p) => ({ ...p, zIndex: 9999 }),
    }),
    [buttonTableStyle],
  );

  const applyListToState = useCallback((res, monthFallback) => {
    if (isErpApiSuccess(res) && res.data && typeof res.data === "object") {
      const mapped = mapSalesTargetsListToRows(res.data, monthFallback);
      setBranchRows(mapped.branchRows);
      setEmployeeRows(mapped.employeeRows);
      return true;
    }
    return false;
  }, []);

  const fetchTargets = useCallback(async () => {
    if (!branchId || !selectedMonth) {
      setBranchRows([]);
      setEmployeeRows([]);
      setLoadingTargets(false);
      return;
    }
    if (!canView) {
      setBranchRows([]);
      setEmployeeRows([]);
      setLoadingTargets(false);
      return;
    }
    setLoadingTargets(true);
    try {
      const res = await listSalesTargets({
        branch_id: branchId,
        month: selectedMonth,
      });
      if (!applyListToState(res, selectedMonth)) {
        setBranchRows([]);
        setEmployeeRows([]);
        if (res?.message && res.success === false) {
          if (!isSalesTargetListViewDeniedMessage(res.message)) {
            toast.error(res.message);
          }
        }
      }
    } catch (err) {
      console.error(err);
      setBranchRows([]);
      setEmployeeRows([]);
      const errMsg = getErpRequestErrorMessage(
        err,
        "Could not load sales targets",
      );
      if (!isSalesTargetListViewDeniedMessage(errMsg)) {
        toast.error(errMsg);
      }
    } finally {
      setLoadingTargets(false);
    }
  }, [branchId, selectedMonth, applyListToState, canView]);

  useEffect(() => {
    void fetchTargets();
  }, [fetchTargets]);

  useEffect(() => {
    let cancelled = false;

    const applyHints = () => {
      setTenantName((tenantNameHint || "").trim() || "—");
      setBranchName((branchNameHint || "").trim() || "—");
    };

    if (!branchId) {
      applyHints();
      return undefined;
    }

    (async () => {
      try {
        const res = await showBranch({ id: branchId });
        if (cancelled) return;
        if (isErpApiSuccess(res)) {
          const { branchName: apiBranchName, tenantName: apiTenantName } =
            parseBranchShowNames(res);
          setBranchName(apiBranchName || branchNameHint || "—");
          setTenantName(apiTenantName || tenantNameHint || "—");
        } else {
          applyHints();
        }
      } catch {
        if (!cancelled) applyHints();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [branchId, tenantNameHint, branchNameHint]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingEmployees(true);
      try {
        const res = await listAdminUsers();
        const rawFromAdmin = Array.isArray(res?.data?.users)
          ? res.data.users
          : [];

        const byId = new Map();
        for (const raw of rawFromAdmin) {
          const e = normalizeEmployeeRow(raw);
          const id = Number(e.ua_id);
          if (!Number.isFinite(id) || id <= 0) continue;
          byId.set(id, e);
        }

        const rows = Array.from(byId.values())
          .filter((e) => employeeMatchesBranchContext(e, tenantId, branchId))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));

        if (!cancelled) setEmployees(rows);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setEmployees([]);
          toast.error("Could not load employees for this branch");
        }
      } finally {
        if (!cancelled) setLoadingEmployees(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, branchId]);

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: e.ua_id,
        label: String(e.name || "").trim() || `User #${e.ua_id}`,
        raw: e,
      })),
    [employees],
  );

  const openAddBranch = () => {
    const existing = branchRows[0];
    if (existing) {
      setEditingBranchRowId(existing.id);
      setBranchFormAmount(String(existing.amount ?? ""));
      setBranchFormNote(String(existing.note ?? ""));
    } else {
      setEditingBranchRowId(null);
      setBranchFormAmount("");
      setBranchFormNote("");
    }
    setShowBranchModal(true);
  };

  const submitBranchModal = async (e) => {
    e.preventDefault();
    if (!canEdit || !branchId) {
      toast.error("You do not have permission to edit sales targets.");
      return;
    }
    const month = String(selectedMonth || "").trim();
    if (!/^\d{4}-\d{2}$/.test(month)) {
      toast.error("Choose a valid month from the page filter");
      return;
    }
    const amt = parseFloat(String(branchFormAmount).replace(/,/g, ""));
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid target amount");
      return;
    }
    const note = String(branchFormNote ?? "").trim();
    setSavingBranch(true);
    try {
      const res = await saveSalesTargets({
        branch_id: branchId,
        month,
        shop_target_amount: amt,
        note,
      });
      if (isErpApiSuccess(res)) {
        const fallback = editingBranchRowId
          ? "Branch sales target updated"
          : "Branch sales target added successfully";
        toast.success(getSafeSuccessMessage(res.message, fallback));
        if (applyListToState(res, month)) {
          /* state updated */
        } else {
          await fetchTargets();
        }
        setShowBranchModal(false);
      } else {
        toast.error(res?.message || "Could not save branch sales target");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        getErpRequestErrorMessage(err, "Could not save branch target"),
      );
    } finally {
      setSavingBranch(false);
    }
  };

  const openAddEmployee = () => {
    setEditingEmpRowId(null);
    setEmpSelected(null);
    setEmpFormAmount("");
    setEmpFormNote("");
    setShowEmpModal(true);
  };

  const openEditEmployee = (row) => {
    setEditingEmpRowId(row.id);
    const opt = employeeOptions.find(
      (o) => Number(o.value) === Number(row.ua_id),
    );
    setEmpSelected(
      opt || {
        value: row.ua_id,
        label: row.employeeName || `User #${row.ua_id}`,
        raw: { ua_id: row.ua_id, name: row.employeeName },
      },
    );
    setEmpFormAmount(String(row.amount ?? ""));
    setEmpFormNote(String(row.note ?? ""));
    setShowEmpModal(true);
  };

  const submitEmployeeModal = async (e) => {
    e.preventDefault();
    if (!canEdit || !branchId) {
      toast.error("You do not have permission to edit sales targets.");
      return;
    }
    if (!empSelected?.value) {
      toast.error("Select an employee");
      return;
    }
    const period = String(selectedMonth || "").trim();
    if (!/^\d{4}-\d{2}$/.test(period)) {
      toast.error("Choose a valid month from the page filter");
      return;
    }
    const amt = parseFloat(String(empFormAmount).replace(/,/g, ""));
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid target amount");
      return;
    }
    const uaId = Number(empSelected.value);
    const noteStr = String(empFormNote ?? "").trim();

    const map = new Map();
    for (const r of employeeRows) {
      map.set(Number(r.ua_id), {
        target_amount: Number(r.amount),
        note: String(r.note ?? "").trim(),
      });
    }
    map.set(uaId, {
      target_amount: amt,
      note: noteStr,
    });
    const employee_targets = Array.from(map.entries()).map(
      ([user_admin_id, v]) => ({
        user_admin_id,
        target_amount: v.target_amount,
        note: v.note,
      }),
    );

    setSavingEmp(true);
    try {
      const res = await saveSalesTargets({
        branch_id: branchId,
        month: period,
        employee_targets,
      });
      if (isErpApiSuccess(res)) {
        const fallback = editingEmpRowId
          ? "Employee sales target updated"
          : "Employee sales target assigned successfully";
        toast.success(getSafeSuccessMessage(res.message, fallback));
        if (!applyListToState(res, period)) await fetchTargets();
        setShowEmpModal(false);
      } else {
        toast.error(res?.message || "Could not save employee sales target");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        getErpRequestErrorMessage(err, "Could not save employee targets"),
      );
    } finally {
      setSavingEmp(false);
    }
  };

  const confirmDeleteBranch = async () => {
    if (!canEdit || !branchId || !deleteShopOpen) return;
    const month = String(selectedMonth || "").trim();
    setDeleting(true);
    try {
      const res = await deleteSalesTarget({
        branch_id: branchId,
        month,
        scope: "SHOP",
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          getSafeSuccessMessage(
            res.message,
            "Branch sales target deleted successfully",
          ),
        );
        await fetchTargets();
        setDeleteShopOpen(false);
      } else {
        toast.error(res?.message || "Could not delete branch sales target");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        getErpRequestErrorMessage(err, "Could not delete branch target"),
      );
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteEmp = async () => {
    if (!canEdit || !branchId || deleteEmpUaId == null) return;
    const month = String(selectedMonth || "").trim();
    setDeleting(true);
    try {
      const res = await deleteSalesTarget({
        branch_id: branchId,
        month,
        scope: "EMPLOYEE",
        user_admin_id: deleteEmpUaId,
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          getSafeSuccessMessage(
            res.message,
            "Employee sales target deleted successfully",
          ),
        );
        await fetchTargets();
        setDeleteEmpUaId(null);
      } else {
        toast.error(res?.message || "Could not delete employee sales target");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        getErpRequestErrorMessage(err, "Could not delete employee target"),
      );
    } finally {
      setDeleting(false);
    }
  };

  const contextMissing = !tenantId || !branchId;
  const cardRounding = buttonTableStyle === "square" ? "rounded-0" : "";
  /** Bordered info panels: explicit radius in modern theme; square stays sharp. */
  const infoBoxRounding =
    buttonTableStyle === "square" ? "rounded-0" : cardClass;

  return (
    <div className="body-wrapper">
      <Toaster position="top-center" />
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass} ${cardRounding}`}
        >
          <div className="px-4 py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <div>
                <h4 className="card-title mb-0">Sales target</h4>
              </div>
            </div>
          </div>

          <div className="card-body p-4">
            {contextMissing && (
              <div className="alert alert-warning py-2 small mb-3">
                Tenant or branch is not set on your profile. API calls require{" "}
                <code>branch_id</code>.
              </div>
            )}

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div
                  className={`border p-3 h-100 bg-body-secondary bg-opacity-25 text-black ${infoBoxRounding}`}
                >
                  <div className="text-uppercase small fw-semibold mb-1">
                    Tenant
                  </div>
                  <div className="fs-5 fw-semibold">{tenantName || "—"}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className={`border p-3 h-100 bg-body-secondary bg-opacity-25 text-black ${infoBoxRounding}`}
                >
                  <div className="text-uppercase small fw-semibold mb-1">
                    Branch
                  </div>
                  <div className="fs-5 fw-semibold">{branchName || "—"}</div>
                </div>
              </div>
            </div>

            <div className="row g-3 align-items-end mb-4">
              <div
                className="col-auto"
                style={{ maxWidth: "min(100%, 220px)" }}
              >
                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold">Target month</Form.Label>
                  <Form.Control
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    disabled={!branchId}
                    className={`w-100 ${cardRounding}`}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Branch targets */}
            <div className={`card mb-4 border ${cardClass} ${cardRounding}`}>
              <div className="card-header bg-transparent border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h4 className="card-title mb-0">Branch sales target</h4>
                <button
                  type="button"
                  className={`btn btn-success ${buttonClass}`}
                  disabled={
                    !canEdit ||
                    !branchId ||
                    loadingTargets ||
                    branchRows.length > 0
                  }
                  title={
                    !canEdit
                      ? "No permission to edit sales targets (SALES_TARGET_EDIT)."
                      : branchRows.length > 0
                        ? "This month already has a branch target — use Edit in the table to change it."
                        : undefined
                  }
                  onClick={openAddBranch}
                >
                  {!canEdit && (
                    <Icon
                      icon={SALES_TARGET_LOCK_ICON}
                      className="me-1"
                      width={LockIconSizeOnButton}
                      height={LockIconSizeOnButton}
                      style={{ color: LockIconWhiteOnButton }}
                      aria-hidden
                    />
                  )}
                  Add Target
                </button>
              </div>
              <div className="card-body p-0">
                <div
                  className={`table-responsive border-top ${cardClass} ${cardRounding}`}
                >
                  <table
                    className={`table mb-0 align-middle ${tableClass} ${cardRounding}`}
                    style={{ tableLayout: "fixed", width: "100%" }}
                  >
                    <colgroup>
                      <col
                        style={{
                          width: showSalesTargetActions ? "25%" : "33.333%",
                        }}
                      />
                      <col
                        style={{
                          width: showSalesTargetActions ? "25%" : "33.333%",
                        }}
                      />
                      <col
                        style={{
                          width: showSalesTargetActions ? "25%" : "33.333%",
                        }}
                      />
                      {showSalesTargetActions && (
                        <col style={{ width: "25%" }} />
                      )}
                    </colgroup>
                    <thead className="text-dark fs-4">
                      <tr>
                        <th>Period</th>
                        <th className="text-end">Target</th>
                        <th className="text-wrap">Note</th>
                        {showSalesTargetActions && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {loadingTargets ? (
                        <tr>
                          <td
                            colSpan={showSalesTargetActions ? 4 : 3}
                            className="text-center py-4 text-muted"
                          >
                            Loading…
                          </td>
                        </tr>
                      ) : branchRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={showSalesTargetActions ? 4 : 3}
                            className="text-center py-4 text-muted"
                          >
                            {!canView ? (
                              <SalesTargetNoViewTableMessage />
                            ) : (
                              <>
                                No branch target for{" "}
                                <strong>
                                  {formatMonthLabel(selectedMonth)}
                                </strong>
                                .
                              </>
                            )}
                          </td>
                        </tr>
                      ) : (
                        branchRows.map((r) => (
                          <tr key={r.id}>
                            <td>{formatMonthLabel(r.period)}</td>
                            <td className="text-end text-black">
                              {formatCurrencyINR(r.amount)}
                            </td>
                            <td className="text-wrap">
                              {String(r.note || "").trim() || "—"}
                            </td>
                            {showSalesTargetActions && (
                              <td>
                                {canEdit ? (
                                  <>
                                    <button
                                      type="button"
                                      title="Edit"
                                      className={`btn btn-primary btn-sm me-1 ${buttonClass}`}
                                      onClick={() => openAddBranch()}
                                    >
                                      <Icon
                                        icon="solar:pen-bold"
                                        width="16"
                                        height="16"
                                      />
                                    </button>
                                    <button
                                      type="button"
                                      title="Delete"
                                      className={`btn btn-outline-danger btn-sm ${buttonClass}`}
                                      onClick={() => setDeleteShopOpen(true)}
                                    >
                                      <Icon
                                        icon="solar:trash-bin-trash-bold"
                                        width="16"
                                        height="16"
                                      />
                                    </button>
                                  </>
                                ) : (
                                  <SalesTargetEditLockedHint />
                                )}
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Employee targets */}
            <div className={`card border ${cardClass} ${cardRounding}`}>
              <div className="card-header bg-transparent border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h4 className="card-title mb-0">Employee sales targets</h4>
                <button
                  type="button"
                  className={`btn btn-primary ${buttonClass}`}
                  disabled={
                    !canEdit ||
                    loadingEmployees ||
                    employeeOptions.length === 0 ||
                    !branchId
                  }
                  title={
                    !canEdit
                      ? "No permission to edit sales targets (SALES_TARGET_EDIT)."
                      : employeeOptions.length === 0
                        ? "No users in directory for this branch"
                        : "Assign target"
                  }
                  onClick={openAddEmployee}
                >
                  {!canEdit && (
                    <Icon
                      icon={SALES_TARGET_LOCK_ICON}
                      className="me-1"
                      width={LockIconSizeOnButton}
                      height={LockIconSizeOnButton}
                      style={{ color: LockIconWhiteOnButton }}
                      aria-hidden
                    />
                  )}
                  Assign Target
                </button>
              </div>
              <div className="card-body p-0">
                {loadingEmployees ? (
                  <div className="text-center py-5 text-muted">Loading…</div>
                ) : !canView ? (
                  <div
                    className={`table-responsive border-top ${cardClass} ${cardRounding}`}
                  >
                    <table
                      className={`table mb-0 align-middle ${tableClass} ${cardRounding}`}
                      style={{ tableLayout: "fixed", width: "100%" }}
                    >
                      <colgroup>
                        <col style={{ width: "33.333%" }} />
                        <col style={{ width: "33.333%" }} />
                        <col style={{ width: "33.333%" }} />
                      </colgroup>
                      <thead className="text-dark fs-4">
                        <tr>
                          <th>Employee</th>
                          <th className="text-end">Target</th>
                          <th className="text-wrap">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            colSpan={3}
                            className="text-center py-4 text-muted"
                          >
                            <SalesTargetNoViewTableMessage />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : employeeOptions.length === 0 ? (
                  <div className="p-4 text-muted small">
                    No users match this tenant/branch in the directory.
                  </div>
                ) : (
                  <div
                    className={`table-responsive border-top ${cardClass} ${cardRounding}`}
                  >
                    <table
                      className={`table mb-0 align-middle ${tableClass} ${cardRounding}`}
                      style={{ tableLayout: "fixed", width: "100%" }}
                    >
                      <colgroup>
                        <col
                          style={{
                            width: showSalesTargetActions ? "25%" : "33.333%",
                          }}
                        />
                        <col
                          style={{
                            width: showSalesTargetActions ? "25%" : "33.333%",
                          }}
                        />
                        <col
                          style={{
                            width: showSalesTargetActions ? "25%" : "33.333%",
                          }}
                        />
                        {showSalesTargetActions && (
                          <col style={{ width: "25%" }} />
                        )}
                      </colgroup>
                      <thead className="text-dark fs-4">
                        <tr>
                          <th>Employee</th>
                          <th className="text-end">Target</th>
                          <th className="text-wrap">Note</th>
                          {showSalesTargetActions && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {loadingTargets ? (
                          <tr>
                            <td
                              colSpan={showSalesTargetActions ? 4 : 3}
                              className="text-center py-4 text-muted"
                            >
                              Loading…
                            </td>
                          </tr>
                        ) : employeeRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={showSalesTargetActions ? 4 : 3}
                              className="text-center py-4 text-muted"
                            >
                              No employee targets for{" "}
                              <strong>{formatMonthLabel(selectedMonth)}</strong>
                              .
                            </td>
                          </tr>
                        ) : (
                          employeeRows.map((r) => (
                            <tr key={r.id}>
                              <td>{r.employeeName}</td>
                              <td className="text-end text-black">
                                {formatCurrencyINR(r.amount)}
                              </td>
                              <td className="text-wrap">
                                {String(r.note || "").trim() || "—"}
                              </td>
                              {showSalesTargetActions && (
                                <td>
                                  {canEdit ? (
                                    <>
                                      <button
                                        type="button"
                                        title="Edit"
                                        className={`btn btn-primary btn-sm me-1 ${buttonClass}`}
                                        onClick={() => openEditEmployee(r)}
                                      >
                                        <Icon
                                          icon="solar:pen-bold"
                                          width="16"
                                          height="16"
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        title="Delete"
                                        className={`btn btn-outline-danger btn-sm ${buttonClass}`}
                                        onClick={() =>
                                          setDeleteEmpUaId(Number(r.ua_id))
                                        }
                                      >
                                        <Icon
                                          icon="solar:trash-bin-trash-bold"
                                          width="16"
                                          height="16"
                                        />
                                      </button>
                                    </>
                                  ) : (
                                    <SalesTargetEditLockedHint />
                                  )}
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showBranchModal}
        onHide={() => !savingBranch && setShowBranchModal(false)}
        centered
        backdrop="static"
        contentClassName={`${cardClass} ${cardRounding}`}
      >
        <Modal.Header closeButton={!savingBranch}>
          <Modal.Title>
            {editingBranchRowId ? "Edit branch target" : "Branch sales target"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitBranchModal}>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Month: <strong>{formatMonthLabel(selectedMonth)}</strong>
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Shop target amount <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="e.g. 500000"
                value={branchFormAmount}
                onChange={(e) => setBranchFormAmount(e.target.value)}
                required
                disabled={savingBranch}
                className={cardRounding}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={branchFormNote}
                onChange={(e) => setBranchFormNote(e.target.value)}
                disabled={savingBranch}
                className={cardRounding}
                placeholder="Enter Note"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="success"
              className={buttonClass}
              type="submit"
              disabled={savingBranch}
            >
              {savingBranch ? "Saving…" : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showEmpModal}
        onHide={() => !savingEmp && setShowEmpModal(false)}
        centered
        backdrop="static"
        size="md"
        contentClassName={`${cardClass} ${cardRounding}`}
      >
        <Modal.Header closeButton={!savingEmp}>
          <Modal.Title>
            {editingEmpRowId
              ? "Edit employee target"
              : "Assign employee target"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitEmployeeModal}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Employee <span className="text-danger">*</span></Form.Label>
              <Select
                styles={selectStyles}
                options={employeeOptions}
                value={empSelected}
                onChange={setEmpSelected}
                isDisabled={!!editingEmpRowId || savingEmp}
                placeholder="Select employee…"
                classNamePrefix="react-select"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Month <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="month"
                value={selectedMonth}
                readOnly
                disabled
                className={cardRounding}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Target amount <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="e.g. 150000"
                value={empFormAmount}
                onChange={(e) => setEmpFormAmount(e.target.value)}
                required
                disabled={savingEmp}
                className={cardRounding}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={empFormNote}
                onChange={(e) => setEmpFormNote(e.target.value)}
                disabled={savingEmp}
                className={cardRounding}
                placeholder="Enter Note"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              className={buttonClass}
              type="submit"
              disabled={savingEmp}
            >
              {savingEmp ? "Saving…" : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={deleteShopOpen}
        onHide={() => !deleting && setDeleteShopOpen(false)}
        centered
        contentClassName={`${cardClass} ${cardRounding}`}
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Remove shop target?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className={buttonClass}
            disabled={deleting}
            onClick={confirmDeleteBranch}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={deleteEmpUaId != null}
        onHide={() => !deleting && setDeleteEmpUaId(null)}
        centered
        contentClassName={`${cardClass} ${cardRounding}`}
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Remove employee target?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className={buttonClass}
            disabled={deleting}
            onClick={confirmDeleteEmp}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SalesTarget;
